import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type Me } from './api';

interface AuthState {
  user: Me | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  const refresh = useCallback(async () => {
    try {
      setUser(await api.get<Me>('/auth/me'));
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setUser(null);
      else throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    await api.post('/auth/logout').catch(() => undefined);
    // Nada de uma conta permanece em memória para a próxima (seção 8.2).
    queryClient.clear();
    setUser(null);
  }, [queryClient]);

  useEffect(() => {
    void refresh();
    const onUnauthorized = () => {
      queryClient.clear();
      setUser(null);
    };
    window.addEventListener('tn:unauthorized', onUnauthorized);
    return () => window.removeEventListener('tn:unauthorized', onUnauthorized);
  }, [refresh, queryClient]);

  return <AuthContext.Provider value={{ user, loading, refresh, logout }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth fora do AuthProvider');
  return ctx;
}

export function useMe(): Me {
  const { user } = useAuth();
  if (!user) throw new Error('Usuário não autenticado');
  return user;
}
